import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { Box, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

const classes = {
    formControl_medium: {
        width: '180px',
    },
}

/**
 * Quantity input component for quote items.
 * Displays a numeric input field for setting the quantity with validation.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {number} props.value - The current quantity value
 * @param {boolean} props.error - Whether there's a validation error
 * @param {boolean} [props.disabled=false] - Whether the input is disabled
 * @param {Object} props.currencyFormat - Currency format settings with decimal and group separators
 * @param {string} props.currencyFormat.decimal - Decimal separator character
 * @param {string} props.currencyFormat.group - Thousands separator character
 * @param {Function} props.onValueChange - Callback when value changes, receives NumericFormat values object
 * @returns {React.ReactElement} A row with label and numeric input for quantity
 */
const QuantityInput = ({ currencyFormat, disabled = false, error, onValueChange, value }) => {
    const { t } = useTranslation()

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-quantity-label">{t('Quantity')}</Typography>
            <NumericFormat
                allowNegative={false}
                customInput={TextField}
                decimalSeparator={currencyFormat.decimal}
                disabled={disabled}
                error={error}
                helperText={error ? t('Quantity cannot be less than 1') : null}
                placeholder="0"
                size="small"
                slotProps={{
                    htmlInput: {
                        style: { textAlign: 'right' },
                        'data-testid': 'nqp-quote-item-quantity',
                    },
                }}
                sx={classes.formControl_medium}
                thousandSeparator={currencyFormat.group}
                value={value}
                variant="outlined"
                onValueChange={(e) => onValueChange(e)}
            />
        </Box>
    )
}

QuantityInput.propTypes = {
    currencyFormat: PropTypes.shape({
        decimal: PropTypes.string.isRequired,
        group: PropTypes.string.isRequired,
    }).isRequired,
    error: PropTypes.bool.isRequired,
    onValueChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    value: PropTypes.number,
}

export default memo(QuantityInput)
